<template>
  <div id="messages" class="relative">
    <MessagesLayout :selected-channel-url="selectedChannelUrl" :show-not-found="queryParamChannelNotFound && !selectedChannelUrl">
      <!-- Channel list for both mobile and desktop -->
      <template #channelList>
        <ChannelList
          :channels="channels"
          :selected-channel-url="selectedChannelUrl"
          :loading="loadingChannels"
          :empty-title="emptyStateConfig.title"
          :empty-message="emptyStateConfig.message"
          :empty-action-text="emptyStateConfig.actionText || ''"
          :empty-action-url="emptyStateConfig.actionUrl || ''"
          :empty-action-to="emptyStateConfig.actionTo || null"
          @select="handleSelectChannel"
        />
      </template>

      <!-- Channel list error (shown above the list) -->
      <template #channelListHeader>
        <div v-if="channelListError" class="p-16 border-b border-red-300 bg-red-50">
          <p class="text-caption text-red-700 mb-8">{{ channelListError }}</p>
          <button class="text-s font-medium text-red-700 hover:text-red-900" @click="fetchChannelList">Retry</button>
        </div>
      </template>

      <!-- Conversation header -->
      <template #header>
        <ChannelHeader
          :title="headerTitle"
          :subtitle="''"
          :avatar-name="headerAvatarName"
          :avatar-url="headerAvatarUrl"
          :show-back="true"
          :current-channel-url="selectedChannelUrl"
          :other-user-information="selectedOtherUser"
          @back="handleSelectChannel('')"
        />
      </template>

      <!-- Message list -->
      <template #messageList>
        <MessageList
          ref="messageList"
          :messages="displayMessages"
          :loading="loadingMessages"
          :hide-empty-state="hideMessageListEmptyState"
          empty-title="No messages yet"
          empty-message="Start the conversation when you are ready."
        />
      </template>

      <!-- Message load error -->
      <template #messageError>
        <div v-if="messageLoadError" class="px-16 py-8 bg-red-50 border-t border-red-300">
          <p class="text-caption text-red-700 mb-8">{{ messageLoadError }}</p>
          <button class="text-s font-medium text-red-700 hover:text-red-900" @click="retryLoadMessages">Retry</button>
        </div>
      </template>

      <!-- Send error -->
      <template #sendError>
        <p v-if="sendError" class="text-caption text-red-600 px-16 pb-8">{{ sendError }}</p>
      </template>

      <!-- Message composer -->
      <template #composer>
        <div v-if="showMessageComposerArea" class="p-8">
          <MessageEmailVerificationComposerBanner
            v-if="messageEmailVerificationRequired"
            @verify="goToMessageEmailVerification"
          />
          <MessageComposer v-else v-model="draftMessage" :loading="sending" @send="handleSend(draftMessage)" />
        </div>
      </template>

      <!-- Not found state -->
      <template #notFound>
        <ConversationNotFound @back="() => handleSelectChannel('')" />
      </template>

      <!-- Empty state when no channel selected -->
      <template #empty>
        <EmptyStateCard
          :title="emptyStateConfig.title"
          :message="emptyStateConfig.message"
          :action-text="emptyStateConfig.actionText"
          :action-url="emptyStateConfig.actionUrl"
          :action-to="emptyStateConfig.actionTo"
          @action="$emit('action')"
        />
      </template>
    </MessagesLayout>
    <UpgradePrompt v-if="isBasicParent && channels.length > 0" />
  </div>
</template>

<script lang="ts">
import { mapState, mapActions, mapGetters } from 'vuex';
import MessagesLayout from '@/components/messages/MessagesLayout.vue';
import EmptyStateCard from '@/components/empty_state_card/EmptyStateCard.vue';
import ChannelList from '@/apps/messages/components/channelList/ChannelList.vue';
import ChannelHeader from '@/apps/messages/components/channelHeader/ChannelHeader.vue';
import ConversationNotFound from '@/apps/messages/components/emptyState/ConversationNotFound.vue';
import MessageList from '@/apps/messages/components/messageList/MessageList.vue';
import MessageComposer from '@/apps/messages/components/messageList/MessageComposer.vue';
import MessageEmailVerificationComposerBanner from '@/apps/messages/components/messageList/MessageEmailVerificationComposerBanner.vue';
import UpgradePrompt from '@/apps/messages/components/UpgradePrompt.vue';
import selectQueryParamChannel from '@/apps/messages/helpers/channelSelection';
import { sendSegmentEvent } from '@/lib/segment';
import type { EmptyStateConfig } from '@/apps/messages/types';
import {
  ensureEmailVerificationOrRedirect,
  resolveEmailVerificationGate,
} from '@/apps/verifyEmail/helpers/emailVerificationGate';
import { buildVerifyEmailPath } from '@/apps/verifyEmail/helpers/paths';
import { markEmailVerificationRequired } from '@/apps/verifyEmail/helpers/pendingEmailVerification';
import { currentAccountId } from '@/apps/verifyEmail/helpers/sessionAccountStorage';
import { clearPendingMessageDraft, getPendingMessageDraft } from '@/apps/verifyEmail/helpers/pendingMessageDraft';
import { redirect } from '@/helpers/redirects';
import {
  heldDraftMessageForChannel,
  saveDraftBeforeVerification,
  shouldClearComposerOnChannelSwitch,
  syncHeldDraftMessage,
} from '@/apps/messages/helpers/pendingMessageDraftHelpers';

const EMPTY_STATE_CONFIGS: Record<'parent' | 'sitter', EmptyStateConfig> = {
  parent: {
    title: 'No messages',
    message: 'Your conversations with sitters will appear here',
    actionText: 'Search sitters',
    actionUrl: '/availability/search',
  },
  sitter: {
    title: 'No messages',
    message:
      "Your conversations with families will appear here after they reach out first or you've applied to their job opening",
    actionText: 'Apply to jobs',
    actionUrl: '/provider/jobs/search',
  },
};

export default {
  name: 'MessagesView',
  components: {
    MessagesLayout,
    EmptyStateCard,
    ChannelList,
    ChannelHeader,
    ConversationNotFound,
    MessageList,
    MessageComposer,
    MessageEmailVerificationComposerBanner,
    UpgradePrompt,
  },
  emits: ['action'],
  data() {
    return {
      draftMessage: '',
      messageEmailVerificationRequired: false,
    };
  },
  computed: {
    ...mapGetters('account', {
      account: 'getAccount',
      accountType: 'accountType',
    }),
    accountId(): string {
      return currentAccountId() || '';
    },
    ...mapState('messages', {
      channels: 'channels',
      selectedChannelUrl: 'currentChannelUrl',
      loadingChannels: 'loadingChannels',
      channelListError: 'channelListError',
      messages: 'messages',
      loadingMessages: 'loadingMessages',
      messageLoadError: 'messageLoadError',
      selectedOtherUser: 'selectedOtherUser',
      selectedChannelMembers: 'selectedChannelMembers',
      sendError: 'sendError',
      sending: 'sending',
      queryParamChannelNotFound: 'queryParamChannelNotFound',
      isMessagingConnected: 'isConnected',
      lastReceivedMessageId: 'lastReceivedMessageId',
      lastReceivedChannelUrl: 'lastReceivedChannelUrl',
      rawChannels: 'rawChannels',
    }),
    displayMessages(): any[] {
      return this.selectedOtherUser?.showMessageHistory === false ? [] : this.messages;
    },
    showComposer(): boolean {
      return Boolean(this.selectedChannelUrl) && !this.selectedOtherUser?.showTrustSafetyBanner;
    },
    showMessageComposerArea(): boolean {
      return this.showComposer;
    },
    messageVerifyEmailPath(): string {
      return buildVerifyEmailPath('/messages');
    },
    headerTitle(): string {
      return this.selectedOtherUser?.name || (this.selectedChannelUrl ? 'Conversation' : 'Messages');
    },
    headerAvatarName(): string {
      return this.selectedOtherUser?.name || '';
    },
    headerAvatarUrl(): string {
      return this.selectedOtherUser?.image || '';
    },
    emptyStateConfig(): EmptyStateConfig {
      return EMPTY_STATE_CONFIGS[this.accountType];
    },
    hideMessageListEmptyState(): boolean {
      return Boolean(this.selectedOtherUser?.showTrustSafetyBanner);
    },
    isBasicParent(): boolean {
      return Boolean(this.account?.isBasic?.() && this.account?.isParent?.());
    },
  },
  watch: {
    // Refreshes messages for the currently viewed channel when new message is received in that channel
    lastReceivedMessageId() {
      if (this.lastReceivedChannelUrl === this.selectedChannelUrl && this.selectedChannelUrl) {
        this.loadMessagesForChannel(this.selectedChannelUrl);
      }
    },
    draftMessage(message: string) {
      this.syncPendingMessageDraft(message);
    },
    account: {
      handler(account) {
        if (account) {
          void this.loadMessageEmailVerificationState();
        }
      },
    },
  },
  created() {
    void this.loadMessageEmailVerificationState();
    this.initializeMessaging();
  },
  beforeUnmount() {
    this.disconnectMessaging();
  },
  methods: {
    ...mapActions('messages', {
      fetchChannelList: 'fetchChannelList',
      selectChannel: 'selectChannel',
      loadMessagesForChannel: 'loadMessagesForChannel',
      sendMessage: 'sendMessage',
      disconnectMessaging: 'disconnectMessaging',
    }),
    async initializeMessaging() {
      await this.$store.dispatch('messages/initializeMessaging');
      if (!this.isMessagingConnected) {
        this.$store.commit('messages/setLoadingMessages', false);
        return;
      }

      // Always fetch fresh channels when component is created
      await this.fetchChannelList();

      // Restore held drafts before uuid deep links — a stale ?uuid= can point at a
      // different conversation than the one the sitter was composing in.
      if (await this.restorePendingMessageDraft()) {
        if (this.selectedChannelUrl) {
          await this.loadMessagesForChannel(this.selectedChannelUrl);
        }
        return;
      }

      // If user is coming from external link with UUID, select that channel
      if (this.$route.query.uuid) {
        this.syncSelectionFromRoute();
        return;
      }

      if (this.selectedChannelUrl) {
        await this.loadMessagesForChannel(this.selectedChannelUrl);
        return;
      }

      this.syncSelectionFromRoute();
    },

    syncSelectionFromRoute(): string | null {
      const { uuid: uuidParam } = this.$route.query as { uuid?: string };
      let routeChannelUrl: string | null = null;
      if (uuidParam && this.rawChannels.length) {
        selectQueryParamChannel({
          uuidParam,
          allChannels: this.rawChannels,
          updateCurrentChannelUrl: (url: string) => {
            routeChannelUrl = url;
            this.handleSelectChannel(url);
          },
          updateCurrentChannelMembers: () => {},
          changeToChannelPanel: () => {},
          updateChannelNotFound: () => {
            this.$store.commit('messages/setQueryParamChannelNotFound', true);
          },
          sendSegmentEvent,
        });
        return routeChannelUrl;
      }

      // Select first channel only on desktop
      // Channel list should be full screen on mobile when user navigates to messages
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile && this.rawChannels.length > 0) {
        this.handleSelectChannel(this.rawChannels[0].url);
      }
      return null;
    },

    async handleSelectChannel(channelUrl: string) {
      if (!channelUrl) {
        // Select first so the draftMessage watcher does not sync an empty body against the held channel.
        await this.selectChannel('');
        this.draftMessage = '';
        return;
      }

      // Select first, then update the composer — clearing draftMessage while still on the held
      // channel would wipe sessionStorage via syncHeldDraftMessage.
      await this.selectChannel(channelUrl);

      const held = heldDraftMessageForChannel(this.accountId, channelUrl);
      if (held !== null) {
        this.draftMessage = held;
      } else if (shouldClearComposerOnChannelSwitch(this.accountId, channelUrl)) {
        this.draftMessage = '';
      }
    },

    async loadMessageEmailVerificationState(): Promise<void> {
      if (!this.account?.isSitter?.()) {
        this.messageEmailVerificationRequired = false;
        return;
      }

      try {
        const decision = await resolveEmailVerificationGate({
          moment: 'parent_interaction',
          returnPath: '/messages',
        });
        this.messageEmailVerificationRequired = decision.requireVerification;
      } catch (error) {
        console.error('Error loading message email verification state:', error);
        this.messageEmailVerificationRequired = false;
      }
    },

    goToMessageEmailVerification(): void {
      const message = this.draftMessage?.trim();
      if (message && this.accountId && this.selectedChannelUrl) {
        const { saved, errorMessage } = saveDraftBeforeVerification({
          accountId: this.accountId,
          channelUrl: this.selectedChannelUrl,
          message,
        });
        if (!saved) {
          if (errorMessage) {
            this.$store.commit('messages/setSendError', errorMessage);
          }
          return;
        }
      }

      markEmailVerificationRequired('/messages');
      redirect(this.messageVerifyEmailPath);
    },

    async handleSend(message: string) {
      if (!this.isMessagingConnected || !this.selectedChannelUrl) return;
      if (!message?.trim()) return;
      if (this.sending) return;

      this.$store.commit('messages/setSending', true);
      try {
        const outcome = await ensureEmailVerificationOrRedirect('parent_interaction', '/messages', () => {
          const { saved, errorMessage } = saveDraftBeforeVerification({
            accountId: this.accountId,
            channelUrl: this.selectedChannelUrl,
            message,
          });
          if (!saved && errorMessage) {
            this.$store.commit('messages/setSendError', errorMessage);
          }
          return saved;
        });
        if (outcome !== 'proceed') {
          return;
        }

        await this.sendMessageAndClearDraft(message);
      } finally {
        this.$store.commit('messages/setSending', false);
      }
    },

    async sendMessageAndClearDraft(message: string): Promise<void> {
      await this.sendMessage(message);
      if (this.sendError) {
        return;
      }

      const pendingDraft = getPendingMessageDraft(this.accountId);
      if (!pendingDraft || pendingDraft.channelUrl === this.selectedChannelUrl) {
        clearPendingMessageDraft();
      }
      this.draftMessage = '';
    },

    async restorePendingMessageDraft(expectedChannelUrl?: string): Promise<boolean> {
      if (!this.accountId) {
        return false;
      }

      const pendingDraft = getPendingMessageDraft(this.accountId);
      if (!pendingDraft) {
        return false;
      }

      if (expectedChannelUrl && pendingDraft.channelUrl !== expectedChannelUrl) {
        return false;
      }

      if (pendingDraft.channelUrl !== this.selectedChannelUrl) {
        await this.selectChannel(pendingDraft.channelUrl);
      }
      this.draftMessage = pendingDraft.message;
      return true;
    },

    syncPendingMessageDraft(message: string) {
      syncHeldDraftMessage(this.accountId, this.selectedChannelUrl, message);
    },

    retryLoadMessages() {
      if (this.selectedChannelUrl) {
        this.loadMessagesForChannel(this.selectedChannelUrl);
      }
    },
  },
};
</script>

<style lang="scss">
main:has(#messages) ~ #onetrust-consent-sdk #ot-sdk-btn-floating {
  display: none;
}

// hide zendesk support icon on messages page, approved by CS
main:has(#messages) ~ div:has(iframe#launcher) {
  display: none !important;
}
</style>
